import patchUpdateCurrency from '../api/patchUpdateCurrency';
import { StatsDisplayMode } from '../types/StatsDisplayMode.type';
import formatCurrency from '../utils/formatCurrency';
import formatTimeToDisplay from '../utils/formatTimeToDisplay';
import React, {
  createContext,
  useContext,
  useRef,
  useReducer,
  ReactNode,
} from 'react';

type ProjectConfig = {
  id: string;
  avgRate: number;
  currency: string;
  display: StatsDisplayMode;
  project: string;
};

const defaultConfig: ProjectConfig = {
  id: '',
  avgRate: 0,
  currency: 'USD',
  display: StatsDisplayMode.HOURS,
  project: '',
};

type ConfigContextType = {
  config: ProjectConfig;
  setConfig: (newConfig: Partial<ProjectConfig>) => void;
  formatValue: (value: number, _?: string) => string;
};

// Create the context with a default value
const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

// Provider component
export const ConfigProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const configRef = useRef(defaultConfig);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const setConfig = (newConfig: Partial<ProjectConfig>) => {
    if (
      newConfig.currency &&
      newConfig.currency !== configRef.current.currency
    ) {
      patchUpdateCurrency(configRef.current.id, newConfig.currency);
    }
    configRef.current = { ...configRef.current, ...newConfig };
    forceUpdate();
  };

  const formatValue = (value: number, _?: string) =>
    configRef.current.display === StatsDisplayMode.COST
      ? formatCurrency(value, configRef.current.currency)
      : formatTimeToDisplay(value);

  const value = {
    config: configRef.current,
    setConfig,
    formatValue,
  };

  return (
    <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
  );
};

// Custom hook to use the config context
// eslint-disable-next-line react-refresh/only-export-components
export const useConfig = (): ConfigContextType => {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};
